// PrintInvoice.js
import React, { useRef, useEffect, useState, Fragment } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useItemsFilter } from "../apis/queries";
import { pageSizes } from "../constants/constant";
import { getImageURL } from "../apis/image";
import "./css/PrintInvoice.css";

const PrintInvoice = ({
    invoice_id,
    clinic_id,
    mode, // "print" or "download"
    setOpenModal,
}) => {
    const [invoiceData, setInvoiceData] = useState(null);
    const [selectedPage, setSelectedPage] = useState("A5");
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const billPrintRef = useRef(null);

    // Extract data values (if available)
    const patienName = invoiceData?.patient_details?.full_name;
    const billNo = invoiceData?.invoice_no;
    const billType = invoiceData?.bill_type;
    const amountInWords = invoiceData?.amount_in_word;
    const invoiceDate = moment(invoiceData?.invoice_date).format("DD-MMM-YYYY");

    // Fetch invoice data
    const { data } = useItemsFilter(
        "payments/billing/",
        `?action=getcreatePDFInvoice&clinic_id=${clinic_id}&backend=True&invoice_data=${invoice_id}`,
        clinic_id && invoice_id ? true : false
    );

    // Set invoice data when available
    useEffect(() => {
        if (data?.invoiceData) {
            setInvoiceData(data?.invoiceData);
        }
    }, [data]);

    // Function to check if all images in the print/download area have loaded
    const checkImagesLoaded = () => {
        if (billPrintRef.current) {
            const imgs = billPrintRef.current.getElementsByTagName("img");
            const totalImages = imgs.length;
            if (totalImages === 0) {
                setImagesLoaded(true);
                return;
            }
            let loadedCount = 0;
            for (let img of imgs) {
                if (img.complete && img.naturalHeight !== 0) {
                    loadedCount++;
                } else {
                    img.onload = img.onerror = () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            setImagesLoaded(true);
                        }
                    };
                }
            }
            if (loadedCount === totalImages) {
                setImagesLoaded(true);
            }
        }
    };

    // Check image load status once invoice data is ready
    useEffect(() => {
        if (invoiceData) {
            checkImagesLoaded();
        }
    }, [invoiceData]);

    // Print functionality using react-to-print
    const handlePrint = useReactToPrint({
        content: () => billPrintRef.current,
    });

    // Download functionality using html2canvas and jsPDF
    const handleDownloadClick = async () => {
        if (!billPrintRef.current) return;
        try {
            // Get the dimensions of the invoice container
            const container = billPrintRef.current;
            const { width, height } = container.getBoundingClientRect();
            // Convert pixels to centimeters (1 cm ≈ 37.795275591 pixels)
            const pxToCm = 1 / 37.795275591;
            const pdfWidth = width * pxToCm;
            const pdfHeight = height * pxToCm;

            // Render the invoice area to canvas at a higher scale for quality and set a white background
            const canvas = await html2canvas(container, {
                scale: 3, // increased scale for higher resolution
                useCORS: true,
                backgroundColor: "#ffffff", // ensure a white background
            });
            const imgData = canvas.toDataURL("image/png");

            // Create a PDF with the exact dimensions of the invoice container
            const pdf = new jsPDF("p", "cm", [pdfWidth, pdfHeight]);
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save("invoice.pdf");
        } catch (error) {
            console.error("Could not generate PDF", error);
        }
    };

    // Auto-trigger the selected action when invoice data is loaded
    useEffect(() => {
        if (invoiceData && mode) {
            if (mode === "download") {
                // Wait for images to load before downloading
                if (imagesLoaded) {
                    setTimeout(() => {
                        handleDownloadClick();
                        setOpenModal(false);
                    }, 500);
                }
            } else if (mode === "print") {
                // For printing, trigger after a slight delay
                setTimeout(() => {
                    handlePrint();
                    setOpenModal(false);
                }, 500);
            }
        }
    }, [invoiceData, imagesLoaded, mode, handlePrint, setOpenModal]);

    // Define a common style for table cells
    const cellStyle = { paddingBottom: "20px" };

    return (
        <div className="opacity-70">
            {/* Container for both print and download */}
            <div
                ref={billPrintRef}
                className="flex justify-center items-center p-4"
            >
                <div className="w-full">
                    {/* Header Image */}
                    <div className="w-full mb-4">
                        <img
                            src={getImageURL(
                                data?.header_footer_setup[selectedPage]
                                    ?.headerUrl
                            )}
                            className="w-full object-cover"
                            alt="Header"
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            onLoad={checkImagesLoaded}
                        />
                    </div>

                    {/* Invoice Title */}
                    <div className="text-center mb-4">
                        <h2 className="text-xl font-bold">Billing Summary</h2>
                    </div>

                    {/* Invoice Header Info */}
                    <div className="flex justify-between text-sm mb-4">
                        <div>
                            <strong>Patient Name:</strong> {patienName}
                        </div>
                        <div>
                            <strong>Date:</strong> {invoiceDate}
                        </div>
                    </div>
                    <div className="flex justify-between text-sm mb-6">
                        <div>
                            <strong>Bill Type:</strong> {billType}
                        </div>
                        <div>
                            <strong>Bill No:</strong> {billNo}
                        </div>
                    </div>

                    {/* Invoice Details Table */}
                    <div className="cu-invoice-pdf-table mb-6 overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-200">
                                    <th
                                        className="border px-4 py-2 text-center"
                                        style={cellStyle}
                                    >
                                        Description
                                    </th>
                                    <th
                                        className="border px-4 py-2 text-center"
                                        style={cellStyle}
                                    >
                                        Qty
                                    </th>
                                    <th
                                        className="border px-4 py-2 text-center"
                                        style={cellStyle}
                                    >
                                        Rate/Package
                                    </th>
                                    <th
                                        className="border px-4 py-2 text-center"
                                        style={cellStyle}
                                    >
                                        Amount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceData?.invoice_lines?.map(
                                    (invcSer, pkgIdx) => {
                                        if (invcSer?.line_package) {
                                            return invcSer?.line_package?.services?.map(
                                                (pkgSer, pkgSerIdx) => (
                                                    <tr
                                                        key={`${pkgSer?.clinic_services_id?.id}${pkgSerIdx}`}
                                                    >
                                                        <td
                                                            className="border px-4 py-2 text-center"
                                                            style={cellStyle}
                                                        >
                                                            {
                                                                pkgSer?.name_of_service
                                                            }
                                                        </td>
                                                        <td
                                                            className="border px-4 py-2 text-center"
                                                            style={cellStyle}
                                                        >
                                                            {pkgSerIdx ===
                                                            Math.ceil(
                                                                invcSer
                                                                    ?.line_package
                                                                    ?.services
                                                                    ?.length / 2
                                                            ) -
                                                                1
                                                                ? invcSer?.quantity ||
                                                                  0
                                                                : ""}
                                                        </td>
                                                        <td
                                                            className="border px-4 py-2 text-center"
                                                            style={cellStyle}
                                                        >
                                                            {pkgSerIdx ===
                                                            Math.ceil(
                                                                invcSer
                                                                    ?.line_package
                                                                    ?.services
                                                                    ?.length / 2
                                                            ) -
                                                                1
                                                                ? invcSer
                                                                      ?.line_package
                                                                      ?.name_of_package
                                                                : ""}
                                                        </td>
                                                        {pkgSerIdx ===
                                                            Math.ceil(
                                                                invcSer
                                                                    ?.line_package
                                                                    ?.services
                                                                    ?.length / 2
                                                            ) -
                                                                1 && (
                                                            <td
                                                                className="border px-4 py-2 text-center"
                                                                style={
                                                                    cellStyle
                                                                }
                                                            >
                                                                {`Rs ${invcSer?.total}`}
                                                            </td>
                                                        )}
                                                    </tr>
                                                )
                                            );
                                        } else if (
                                            invcSer?.line_service?.name_of_service?.toUpperCase() ===
                                                "MEDICINE CHARGES" ||
                                            invcSer?.line_service?.name_of_service?.toUpperCase() ===
                                                "MEDICINE CHARGE"
                                        ) {
                                            return (
                                                <Fragment
                                                    key={`${invcSer?.line_service?.id}${pkgIdx}`}
                                                >
                                                    <tr
                                                        className="bg-gray-100"
                                                        style={cellStyle}
                                                    >
                                                        <td
                                                            className="border px-4 py-2 text-center font-semibold"
                                                            style={cellStyle}
                                                        >
                                                            {
                                                                invcSer
                                                                    ?.line_service
                                                                    ?.name_of_service
                                                            }
                                                        </td>
                                                        <td
                                                            className="border px-4 py-2 text-center font-semibold"
                                                            style={cellStyle}
                                                        >
                                                            {invcSer?.type ===
                                                            "Qty"
                                                                ? invcSer?.quantity
                                                                : "-"}
                                                        </td>
                                                        <td
                                                            className="border px-4 py-2 text-center font-semibold"
                                                            style={cellStyle}
                                                        >
                                                            {Number(
                                                                invcSer?.price
                                                            ) > 0
                                                                ? invcSer?.price
                                                                : "-"}
                                                        </td>
                                                        <td
                                                            className="border px-4 py-2 text-center font-semibold"
                                                            style={cellStyle}
                                                        >
                                                            -
                                                        </td>
                                                    </tr>
                                                    {invcSer?.sub_lines?.map(
                                                        (subline, subIdx) => (
                                                            <tr key={subIdx}>
                                                                <td
                                                                    className="border px-4 py-2 text-center text-sm"
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {`${subline?.medicine_name} `}
                                                                    {subline?.generic_name ===
                                                                        "nan" ||
                                                                    !subline?.generic_name
                                                                        ? ""
                                                                        : `(${subline?.generic_name})`}
                                                                    {subline?.batch_no?.map(
                                                                        (
                                                                            ele
                                                                        ) => {
                                                                            if (
                                                                                ele?.consumed_qty >
                                                                                0
                                                                            ) {
                                                                                return (
                                                                                    <div
                                                                                        className="text-xs"
                                                                                        key={
                                                                                            ele?.batchId
                                                                                        }
                                                                                    >
                                                                                        {`Batch No: ${ele?.batchNo}, Qty: ${ele?.consumed_qty}, expiry: ${moment(ele?.expiry).format("DD MMM YYYY")}`}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        }
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className="border px-4 py-2 text-center"
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {subline?.quantity ||
                                                                        0}
                                                                </td>
                                                                <td
                                                                    className="border px-4 py-2 text-center"
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {
                                                                        subline?.price
                                                                    }
                                                                </td>
                                                                <td
                                                                    className="border px-4 py-2 text-center"
                                                                    style={
                                                                        cellStyle
                                                                    }
                                                                >
                                                                    {`Rs ${Number(subline?.price) * Number(subline?.quantity)}`}
                                                                </td>
                                                            </tr>
                                                        )
                                                    )}
                                                </Fragment>
                                            );
                                        } else {
                                            return (
                                                <tr
                                                    key={`${invcSer?.line_service?.id}${pkgIdx}`}
                                                >
                                                    <td
                                                        className="border px-4 py-2 text-center"
                                                        style={cellStyle}
                                                    >
                                                        {
                                                            invcSer
                                                                ?.line_service
                                                                ?.name_of_service
                                                        }
                                                    </td>
                                                    <td
                                                        className="border px-4 py-2 text-center"
                                                        style={cellStyle}
                                                    >
                                                        {invcSer?.type === "Qty"
                                                            ? invcSer?.quantity
                                                            : "-"}
                                                    </td>
                                                    <td
                                                        className="border px-4 py-2 text-center"
                                                        style={cellStyle}
                                                    >
                                                        {Number(
                                                            invcSer?.price
                                                        ) > 0
                                                            ? invcSer?.price
                                                            : "-"}
                                                    </td>
                                                    <td
                                                        className="border px-4 py-2 text-center"
                                                        style={cellStyle}
                                                    >
                                                        {`Rs ${invcSer?.total}`}
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    }
                                )}
                                <tr>
                                    <td
                                        colSpan="3"
                                        className="border px-4 py-2 font-bold text-center"
                                        style={cellStyle}
                                    >
                                        Total
                                    </td>
                                    <td
                                        className="border px-4 py-2 text-center font-bold"
                                        style={cellStyle}
                                    >
                                        {`Rs ${invoiceData?.invoice_total}`}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Total in Words */}
                    <div className="text-center my-4">
                        <div className="text-lg font-semibold">
                            Total Amount in words:
                        </div>
                        <div className="text-base">{amountInWords} Only.</div>
                    </div>

                    {/* System Generated Notice */}
                    <div className="text-center my-2 text-xs text-gray-600">
                        This is a system generated invoice.
                    </div>

                    {/* Signature Section */}
                    <div className="flex flex-col items-end mt-6">
                        {data?.header_footer_setup[selectedPage]
                            ?.printSignature && (
                            <div
                                className="overflow-hidden"
                                style={{
                                    height: `${data?.header_footer_setup[selectedPage]?.signatureHeight}cm`,
                                }}
                            >
                                <div
                                    style={{
                                        width: `${data?.header_footer_setup[selectedPage]?.signatureWidth}cm`,
                                    }}
                                    className="m-0 p-0"
                                >
                                    <img
                                        src={getImageURL(
                                            data?.header_footer_setup[
                                                selectedPage
                                            ]?.signatureImg
                                        )}
                                        className="m-0 p-0"
                                        alt="Signature"
                                        crossOrigin="anonymous"
                                        referrerPolicy="no-referrer"
                                        onLoad={checkImagesLoaded}
                                    />
                                    {data?.header_footer_setup[
                                        selectedPage
                                    ]?.signatureText
                                        ?.split("\n")
                                        .map((line, index) => (
                                            <div
                                                key={index}
                                                className="text-xs"
                                            >
                                                {line}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintInvoice;
